import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BadgeDollarSign, CheckCircle2, HandCoins, Loader2, Pencil, Plus, RotateCcw, Tag, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, fmtDate, today } from "@/lib/api";
import { downloadCsv } from "@/lib/exportCsv";
import { PageHeader, EmptyState, Money, TypeBadge } from "@/components/common";
import { PAYMENT_TYPES, SALE_PAYMENT_TYPES, can } from "@/lib/labels";
import { SaleDialog } from "@/components/dialogs";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const inputCls = "h-10 bg-[#0F0F12] border-[#1A1A22] focus:border-blue-500 focus:ring-1 focus:ring-blue-500";
const labelCls = "text-xs font-semibold uppercase tracking-[0.15em] text-zinc-400";

export default function Sales() {
  const { user } = useAuth();
  const [sales, setSales] = useState(null);
  const [cars, setCars] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [branches, setBranches] = useState([]);
  const [open, setOpen] = useState(false);
  const [receiveSale, setReceiveSale] = useState(null);
  const [markPaidSale, setMarkPaidSale] = useState(null);
  const [priceSale, setPriceSale] = useState(null);
  const [editSale, setEditSale] = useState(null);
  const [refundSale, setRefundSale] = useState(null);
  const [month, setMonth] = useState("");
  const [branchFilter, setBranchFilter] = useState("all");
  const [creatorFilter, setCreatorFilter] = useState("all");

  const load = useCallback(() => {
    Promise.all([api.get("/sales"), api.get("/cars"), api.get("/purchases")]).then(([s, c, p]) => {
      setSales(s.data);
      setCars(c.data);
      setPurchases(p.data);
    });
  }, []);
  useEffect(() => { load(); api.get("/branches").then((r) => setBranches(r.data)); }, [load]);

  const purchasedIds = new Set(purchases.filter((p) => p.status !== "cancelled").map((p) => p.car_id));
  const eligible = cars.filter((c) => (purchasedIds.has(c.id) || c.is_konsinyasi) && !["terjual", "booking"].includes(c.status));
  const activeSales = (sales || []).filter((s) => s.status !== "cancelled");
  const filteredSales = (sales || []).filter((s) => (!month || s.date.startsWith(month)) && (branchFilter === "all" || s.branch_id === branchFilter) && (creatorFilter === "all" || s.created_by === creatorFilter));
  const creators = [...new Map((sales || []).filter((s) => s.created_by).map((s) => [s.created_by, s.created_by_name || "-"])).entries()];

  const exportSales = () => {
    const carName = (id) => {
      const c = cars.find((x) => x.id === id);
      return c ? `${c.merk} ${c.model} ${c.color || ""} ${c.year || ""}`.trim() : "-";
    };
    const branchName = (id) => branches.find((b) => b.id === id)?.name || "-";
    downloadCsv(`penjualan-${month || "semua"}.csv`,
      ["Tanggal", "Mobil", "Pembeli", "Tipe", "Harga Jual", "Dana Diterima", "Komisi", "Cabang", "Pencatat"],
      filteredSales.map((s) => [s.date, carName(s.car_id), s.buyer_name, PAYMENT_TYPES[s.payment_type] || s.payment_type, s.price, s.received_amount || 0, s.commission || 0, branchName(s.branch_id), s.created_by_name || "-"]));
    toast.success("File Excel (CSV) penjualan diunduh");
  };
  const filteredActive = filteredSales.filter((s) => s.status !== "cancelled");
  const totalPenjualan = filteredActive.reduce((a, s) => a + s.price, 0);
  const totalKomisi = filteredActive.reduce((a, s) => a + (s.commission || 0), 0);

  const remove = async (id) => {
    if (!window.confirm("Hapus penjualan ini? Status mobil kembali Siap Jual.")) return;
    try {
      await api.delete(`/sales/${id}`);
      toast.success("Penjualan dihapus");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div data-testid="sales-page">
      <PageHeader title="Penjualan" subtitle="Transaksi Cash / Leasing, pembayaran bertahap & komisi penjual">
        <Button data-testid="export-sales-button" variant="outline" onClick={exportSales} className="border-[#26262E] text-zinc-300 hover:bg-[#121218] h-10">
          Unduh Excel
        </Button>
        <Select value={branchFilter} onValueChange={setBranchFilter}>
          <SelectTrigger data-testid="sale-branch-filter" className="w-44 h-10 bg-[#0F0F12] border-[#1A1A22]"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#0A0A0C] border-[#1A1A22]">
            <SelectItem value="all">Semua Cabang</SelectItem>
            {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={creatorFilter} onValueChange={setCreatorFilter}>
          <SelectTrigger data-testid="sale-creator-filter" className="w-44 h-10 bg-[#0F0F12] border-[#1A1A22]"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#0A0A0C] border-[#1A1A22]">
            <SelectItem value="all">Semua Pencatat</SelectItem>
            {creators.map(([id, name]) => <SelectItem key={id} value={id}>{name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input data-testid="sale-month-filter" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44 h-10 bg-[#0F0F12] border-[#1A1A22]" />
        {can(user, "input_penjualan") && (
          <Button data-testid="add-sale-button" onClick={() => setOpen(true)} disabled={eligible.length === 0} className="bg-blue-600 hover:bg-blue-500 text-white shadow-[0_0_12px_rgba(37,99,235,0.25)] font-semibold rounded-md">
            <Plus className="w-4 h-4 mr-1" /> Catat Penjualan
          </Button>
        )}
        {can(user, "input_penjualan") && eligible.length === 0 && (sales || []).length >= 0 && (
          <p data-testid="no-sellable-stock-hint" className="text-xs text-zinc-500">Tidak ada stok Siap Jual — unit booking/terjual tidak bisa dijual lagi</p>
        )}
      </PageHeader>

      {!sales ? (
        <div className="h-64 bg-[#0A0A0C] border border-[#1A1A22] rounded-md animate-pulse" />
      ) : sales.length === 0 ? (
        <EmptyState icon={BadgeDollarSign} title="Belum ada penjualan" desc="Catat penjualan pertama Anda" />
      ) : (
        <>
          <div className="space-y-3 sm:hidden" data-testid="sales-mobile-list">
            {filteredSales.map((sale) => (
              <article
                key={sale.id}
                data-testid={`mobile-sale-card-${sale.id}`}
                className="border border-[#1A1A22] bg-[#0A0A0C] p-4"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <Link
                      to={`/mobil/${sale.car_id}`}
                      className="block break-words font-medium text-zinc-100"
                    >
                      {sale.car_name}
                    </Link>
                    <p className="mt-1 text-xs text-zinc-500">
                      {sale.car_plate || "Tanpa plat"} · {sale.buyer_name}
                    </p>
                  </div>
                  <TypeBadge label={PAYMENT_TYPES[sale.payment_type]} />
                </div>
                <div className="mt-4 grid grid-cols-2 gap-3 border-y border-[#1A1A22] py-3">
                  <div>
                    <p className="text-[10px] font-semibold uppercase text-zinc-500">Harga Jual</p>
                    <Money
                      data-testid={`mobile-sale-price-${sale.id}`}
                      value={sale.price}
                      className="mt-1 text-emerald-500"
                    />
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold uppercase text-zinc-500">Tanggal</p>
                    <p data-testid={`mobile-sale-date-${sale.id}`} className="mt-1 text-sm text-zinc-300">
                      {fmtDate(sale.date)}
                    </p>
                    {sale.paid_date && (
                      <p className="mt-1 text-xs font-semibold text-emerald-500">
                        Lunas {fmtDate(sale.paid_date)}
                      </p>
                    )}
                  </div>
                </div>
                {sale.status !== "cancelled" && (
                  <div className="mt-3 flex flex-wrap justify-end gap-2">
                    {(user?.role !== "admin_cabang" || sale.created_by === user?.id) && (
                      <Button
                        data-testid={`mobile-receive-${sale.id}`}
                        variant="outline"
                        size="sm"
                        onClick={() => setReceiveSale(sale)}
                        className="border-[#26262E] text-zinc-300"
                      >
                        <HandCoins className="mr-1 h-3.5 w-3.5" /> Dana
                      </Button>
                    )}
                    {can(user, "aksi_penjualan") && !sale.paid_off && sale.price > 0 && (
                      <Button
                        data-testid={`mobile-mark-paid-${sale.id}`}
                        variant="outline"
                        size="sm"
                        onClick={() => setMarkPaidSale(sale)}
                        className="border-emerald-600/40 text-emerald-500"
                      >
                        <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Lunas
                      </Button>
                    )}
                    {can(user, "aksi_penjualan") && (
                      <Button
                        data-testid={`mobile-edit-sale-${sale.id}`}
                        variant="outline"
                        size="sm"
                        onClick={() => setEditSale(sale)}
                        className="border-[#26262E] text-zinc-300"
                      >
                        <Pencil className="mr-1 h-3.5 w-3.5" /> Edit
                      </Button>
                    )}
                  </div>
                )}
              </article>
            ))}
            {filteredSales.length === 0 && (
              <p data-testid="sales-mobile-empty" className="py-8 text-center text-sm text-zinc-500">
                Tidak ada penjualan pada bulan ini.
              </p>
            )}
            <div data-testid="sales-mobile-total" className="border-y border-[#1A1A22] py-4">
              <div className="flex items-center justify-between gap-3">
                <span className="text-xs font-semibold uppercase text-zinc-500">Total Penjualan</span>
                <Money value={totalPenjualan} className="font-semibold text-emerald-500" />
              </div>
            </div>
          </div>
          <div className="hidden overflow-hidden rounded-md border border-[#1A1A22] bg-[#0A0A0C] fade-up sm:block">
            <table className="w-full table-fixed text-sm" data-testid="sales-table">
            <thead>
              <tr className="border-b border-[#1A1A22] text-left">
                <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Mobil</th>
                <th className="hidden px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500 sm:table-cell">Pembeli</th>
                <th className="hidden px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500 sm:table-cell">Tipe</th>
                <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500 text-right">Harga Jual</th>
                <th className="hidden px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500 text-right sm:table-cell">Komisi</th>
                <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Tanggal</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {filteredSales.map((s) => (
                <tr key={s.id} className="border-b border-[#1A1A22]/60 last:border-0 hover:bg-[#121218]/50 transition-colors duration-200">
                  <td className="break-words px-4 py-3">
                    <Link to={`/mobil/${s.car_id}`} className="font-medium text-zinc-100 hover:text-blue-500 transition-colors duration-200">{s.car_name}</Link>
                    <p className="text-xs text-zinc-500">{s.car_plate}</p>
                  </td>
                  <td className="hidden break-words px-4 py-3 text-zinc-300 sm:table-cell">{s.buyer_name}
                    {s.buyer_phone && <p className="text-xs text-zinc-500" data-testid={`buyer-phone-${s.id}`}>{s.buyer_phone}</p>}
                    {s.salesperson_name && <p className="text-xs text-zinc-500">Sales: {s.salesperson_name}</p>}
                    {s.created_by_name && <p className="text-[10px] text-zinc-600">dicatat oleh {s.created_by_name}</p>}
                  </td>
                  <td className="hidden px-4 py-3 sm:table-cell">
                    <TypeBadge label={PAYMENT_TYPES[s.payment_type]} />
                    {s.status === "cancelled" && <span className="ml-1 inline-flex px-2 py-0.5 rounded-sm text-[11px] font-semibold uppercase tracking-wider border text-rose-500 bg-rose-500/10 border-rose-500/20">Dibatalkan</span>}
                    {s.leasing_name && <p className="text-xs text-zinc-500 mt-1">{s.leasing_name}</p>}
                    {s.payment_type === "dp" && <p className="text-xs text-zinc-500 mt-1">DP {new Intl.NumberFormat("id-ID").format(s.dp_amount)}</p>}
                  </td>
                  <td className="break-all px-4 py-3 text-right">
                    {s.price ? (
                      <>
                        <Money value={s.price} className="text-emerald-500" />
                        {!s.price_locked && <p className="text-[10px] text-blue-500 mt-0.5">sementara · dari dana diterima</p>}
                      </>
                    ) : <span className="text-blue-500 text-xs font-semibold uppercase tracking-wider">Belum Ditentukan</span>}
                    {(s.received_amount || 0) > 0 && <p className="text-[10px] text-zinc-500 mt-0.5 font-mono">Diterima {new Intl.NumberFormat("id-ID").format(s.received_amount)}</p>}
                    {(s.refunded_amount || 0) > 0 && <p className="text-[10px] text-rose-500 mt-0.5 font-mono">Refund {new Intl.NumberFormat("id-ID").format(s.refunded_amount)}</p>}
                    {s.paid_date && (
                      <p className="text-[10px] text-emerald-500 font-semibold mt-0.5">
                        Lunas {fmtDate(s.paid_date)}
                      </p>
                    )}
                  </td>
                  <td className="hidden px-4 py-3 text-right sm:table-cell">{s.commission ? <Money value={s.commission} className="text-zinc-300" /> : <span className="text-zinc-600">-</span>}</td>
                  <td className="px-4 py-3 text-zinc-400">
                    {fmtDate(s.date)}
                    {s.paid_date && <p className="text-xs text-emerald-500">Lunas {fmtDate(s.paid_date)}</p>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex flex-wrap justify-end gap-1">
                      {s.status !== "cancelled" && (
                        <>
                          {(user?.role !== "admin_cabang" || s.created_by === user?.id) && (
                          <button data-testid={`receive-${s.id}`} title="Terima Dana ke Kas" onClick={() => setReceiveSale(s)} className="p-1.5 text-zinc-500 hover:text-emerald-500 transition-colors duration-200">
                            <HandCoins className="w-4 h-4" />
                          </button>
                          )}
                          {can(user, "aksi_penjualan") && !s.paid_off && s.price > 0 && (s.received_amount || 0) < s.price && (
                            <button data-testid={`mark-paid-${s.id}`} title="Tandai Lunas (Terjual)" onClick={() => setMarkPaidSale(s)} className="p-1.5 text-zinc-500 hover:text-emerald-400 transition-colors duration-200">
                              <CheckCircle2 className="w-4 h-4" />
                            </button>
                          )}
                          {(can(user, "aksi_penjualan") || can(user, "refund_penjualan")) && (user?.role !== "admin_cabang" || s.created_by === user?.id) && (
                            <button data-testid={`refund-sale-${s.id}`} title="Refund Penjualan" onClick={() => setRefundSale(s)} className="p-1.5 text-zinc-500 hover:text-rose-400 transition-colors duration-200">
                              <RotateCcw className="w-4 h-4" />
                            </button>
                          )}
                          {can(user, "aksi_penjualan") && (
                            <button data-testid={`edit-sale-${s.id}`} title="Edit Penjualan" onClick={() => setEditSale(s)} className="p-1.5 text-zinc-500 hover:text-blue-500 transition-colors duration-200">
                              <Pencil className="w-4 h-4" />
                            </button>
                          )}
                        </>
                      )}
                      {can(user, "aksi_penjualan") && !s.price && (
                        <button data-testid={`set-price-${s.id}`} title="Set Harga Final" onClick={() => setPriceSale(s)} className="p-1.5 text-zinc-500 hover:text-blue-500 transition-colors duration-200">
                          <Tag className="w-4 h-4" />
                        </button>
                      )}
                      {user?.role === "admin" && (
                        <button data-testid={`delete-sale-${s.id}`} onClick={() => remove(s.id)} className="text-zinc-600 hover:text-rose-500 transition-colors duration-200 p-1.5">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {filteredSales.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-sm text-zinc-500">Tidak ada penjualan pada bulan ini.</td></tr>
              )}
              <tr className="bg-[#050505]/50">
                <td colSpan={3} className="px-4 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500">Total</td>
                <td className="px-4 py-3 text-right"><Money value={totalPenjualan} className="text-emerald-500 font-semibold" /></td>
                <td className="px-4 py-3 text-right"><Money value={totalKomisi} className="text-zinc-300 font-semibold" /></td>
                <td colSpan={2} />
              </tr>
            </tbody>
            </table>
          </div>
        </>
      )}

      <SaleDialog open={open} onOpenChange={setOpen} cars={eligible} onSaved={load} />
      <ReceiveDialog sale={receiveSale} branches={branches} onClose={() => setReceiveSale(null)} onSaved={load} />
      <MarkPaidDialog sale={markPaidSale} onClose={() => setMarkPaidSale(null)} onSaved={load} />
      <PriceDialog sale={priceSale} onClose={() => setPriceSale(null)} onSaved={load} />
      <RefundDialog sale={refundSale} branches={branches} onClose={() => setRefundSale(null)} onSaved={load} />
      <EditSaleDialog sale={editSale} onClose={() => setEditSale(null)} onSaved={load} />
    </div>
  );
}

export function RefundDialog({ sale, branches, onClose, onSaved }) {
  const { user } = useAuth();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const visible = user?.role === "admin_cabang" ? branches.filter((b) => b.id === user.branch_id || b.is_pusat) : branches;

  useEffect(() => {
    if (sale) {
      const refundable = Math.max(0, (sale.received_amount || 0) - (sale.refunded_amount || 0));
      setForm({ amount: String(refundable), date: today(), cash_branch_id: user?.role === "admin_cabang" ? user.branch_id : sale.cash_branch_id || branches[0]?.id || "", reason: "" });
    }
  }, [sale, user, branches]);

  if (!sale || !form) return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/sales/${sale.id}/refund`, { ...form, amount: parseFloat(form.amount) || 0, cash_branch_id: form.cash_branch_id || null, cancel_sale: true });
      toast.success("Refund tercatat & mobil kembali Siap Jual");
      onClose();
      onSaved();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!sale} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0A0A0C] border-[#1A1A22] text-zinc-100 max-w-md">
        <DialogHeader><DialogTitle className="font-heading text-xl font-bold">Refund Penjualan</DialogTitle></DialogHeader>
        <p className="text-sm text-zinc-400 -mt-2">{sale.car_name} · Pembeli: {sale.buyer_name}</p>
        <form onSubmit={submit} className="space-y-4" data-testid="refund-form">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className={labelCls}>Jumlah Refund (Rp)</Label>
              <Input data-testid="refund-amount-input" type="number" min="1" required value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} className={inputCls} />
            </div>
            <div className="space-y-2">
              <Label className={labelCls}>Tanggal</Label>
              <Input data-testid="refund-date-input" type="date" required value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} className={inputCls} />
            </div>
          </div>
          <div className="space-y-2">
            <Label className={labelCls}>Refund Keluar dari Kas</Label>
            <Select value={form.cash_branch_id} onValueChange={(v) => setForm((f) => ({ ...f, cash_branch_id: v }))}>
              <SelectTrigger data-testid="refund-branch-select" className={inputCls}><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#0A0A0C] border-[#1A1A22]">
                {user?.role !== "admin_cabang" && <SelectItem value="umum">Kas Umum (Tanpa Cabang)</SelectItem>}
                {visible.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className={labelCls}>Alasan Refund</Label>
            <Input data-testid="refund-reason-input" value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} placeholder="Contoh: pembeli batal, unit bermasalah" className={inputCls} />
          </div>
          <p className="text-xs text-zinc-500">Refund membatalkan penjualan dan mobil kembali ke status Siap Jual. Dana refund tercatat sebagai kas keluar.</p>
          <Button data-testid="refund-submit-button" type="submit" disabled={busy} className="w-full bg-rose-600 hover:bg-rose-500 text-white font-semibold">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Refund & Kembalikan ke Stok"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditSaleDialog({ sale, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (sale) {
      setForm({
        payment_type: sale.payment_type === "dp" ? "cash" : sale.payment_type,
        price: sale.price || "", dp_amount: sale.dp_amount || "", leasing_name: sale.leasing_name || "",
        buyer_name: sale.buyer_name || "", buyer_phone: sale.buyer_phone || "", salesperson_name: sale.salesperson_name || "",
        commission: sale.commission || "", date: sale.date, paid_date: sale.paid_date || "",
        notes: sale.notes || "",
      });
    }
  }, [sale]);

  if (!sale || !form) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put(`/sales/${sale.id}`, {
        payment_type: form.payment_type,
        price: parseFloat(form.price) || 0,
        dp_amount: parseFloat(form.dp_amount) || 0,
        commission: parseFloat(form.commission) || 0,
        leasing_name: form.leasing_name,
        buyer_name: form.buyer_name,
        buyer_phone: form.buyer_phone,
        salesperson_name: form.salesperson_name,
        date: form.date,
        paid_date: form.paid_date || null,
        notes: form.notes,
      });
      toast.success("Penjualan diperbarui");
      onClose();
      onSaved();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!sale} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0A0A0C] border-[#1A1A22] text-zinc-100 max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading text-xl font-bold">Edit Penjualan</DialogTitle></DialogHeader>
        <p className="text-sm text-zinc-400 -mt-2">{sale.car_name}</p>
        <form onSubmit={submit} className="space-y-4" data-testid="edit-sale-form">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className={labelCls}>Tipe Pembayaran</Label>
              <Select value={form.payment_type} onValueChange={(v) => set("payment_type", v)}>
                <SelectTrigger data-testid="edit-sale-type-select" className={inputCls}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0A0A0C] border-[#1A1A22]">
                  {Object.entries(SALE_PAYMENT_TYPES).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className={labelCls}>Tanggal</Label>
              <Input data-testid="edit-sale-date-input" type="date" required value={form.date} onChange={(e) => set("date", e.target.value)} className={inputCls} />
            </div>
          </div>
          <div className="space-y-2">
            <Label className={labelCls}>Harga Jual (Rp)</Label>
            <Input data-testid="edit-sale-price-input" type="number" min="0" value={form.price} onChange={(e) => set("price", e.target.value)} className={inputCls} />
          </div>
          {(sale.paid_off || (sale.price > 0 && sale.received_amount >= sale.price)) && (
            <div className="space-y-2">
              <Label className={labelCls}>Tanggal Lunas</Label>
              <Input
                data-testid="edit-sale-paid-date-input"
                type="date"
                required
                value={form.paid_date}
                onChange={(e) => set("paid_date", e.target.value)}
                className={inputCls}
              />
            </div>
          )}
          <div className="space-y-2">
            <Label className={labelCls}>DP / Pembayaran Awal (Rp)</Label>
            <Input data-testid="edit-sale-dp-input" type="number" min="0" value={form.dp_amount} onChange={(e) => set("dp_amount", e.target.value)} className={inputCls} />
          </div>
          {form.payment_type === "leasing" && (
            <div className="space-y-2">
              <Label className={labelCls}>Nama Leasing</Label>
              <Input data-testid="edit-sale-leasing-input" value={form.leasing_name} onChange={(e) => set("leasing_name", e.target.value)} className={inputCls} />
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className={labelCls}>Nama Pembeli</Label>
              <Input data-testid="edit-sale-buyer-input" required value={form.buyer_name} onChange={(e) => set("buyer_name", e.target.value)} className={inputCls} />
            </div>
            <div className="space-y-2">
              <Label className={labelCls}>No HP Pembeli</Label>
              <Input data-testid="edit-sale-buyer-phone-input" value={form.buyer_phone} onChange={(e) => set("buyer_phone", e.target.value)} placeholder="08xxxxxxxxxx" className={inputCls} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className={labelCls}>Nama Penjual (Sales)</Label>
              <Input data-testid="edit-sale-salesperson-input" value={form.salesperson_name} onChange={(e) => set("salesperson_name", e.target.value)} className={inputCls} />
            </div>
            <div className="space-y-2">
              <Label className={labelCls}>Komisi Penjual (Rp)</Label>
              <Input data-testid="edit-sale-commission-input" type="number" min="0" value={form.commission} onChange={(e) => set("commission", e.target.value)} className={inputCls} />
            </div>
          </div>
          <Button data-testid="edit-sale-submit-button" type="submit" disabled={busy} className="w-full bg-blue-600 hover:bg-blue-500 text-white shadow-[0_0_12px_rgba(37,99,235,0.25)] font-semibold">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan Perubahan"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function ReceiveDialog({ sale, branches, onClose, onSaved }) {
  const { user } = useAuth();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const visible = user?.role === "admin_cabang" ? branches.filter((b) => b.id === user.branch_id || b.is_pusat) : branches;

  useEffect(() => {
    if (sale) setForm({ amount: "", date: today(), cash_branch_id: user?.role === "admin_cabang" ? user.branch_id : sale.cash_branch_id || sale.branch_id || branches[0]?.id || "", description: "" });
  }, [sale, user, branches]);

  if (!sale || !form) return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/sales/${sale.id}/receive`, { ...form, amount: parseFloat(form.amount) || 0 });
      toast.success("Dana penjualan diterima ke buku kas");
      onClose();
      onSaved();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!sale} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0A0A0C] border-[#1A1A22] text-zinc-100 max-w-md">
        <DialogHeader><DialogTitle className="font-heading text-xl font-bold">Terima Dana Penjualan</DialogTitle></DialogHeader>
        <p className="text-sm text-zinc-400 -mt-2">{sale.car_name} · Pembeli: {sale.buyer_name}</p>
        <form onSubmit={submit} className="space-y-4" data-testid="receive-form">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className={labelCls}>Jumlah Diterima (Rp)</Label>
              <Input data-testid="receive-amount-input" type="number" min="1" required value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} placeholder="50000000" className={inputCls} />
            </div>
            <div className="space-y-2">
              <Label className={labelCls}>Tanggal</Label>
              <Input data-testid="receive-date-input" type="date" required value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} className={inputCls} />
            </div>
          </div>
          <div className="space-y-2">
            <Label className={labelCls}>Masuk ke Kas</Label>
            <Select value={form.cash_branch_id} onValueChange={(v) => setForm((f) => ({ ...f, cash_branch_id: v }))}>
              <SelectTrigger data-testid="receive-branch-select" className={inputCls}><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#0A0A0C] border-[#1A1A22]">
                <SelectItem value="umum">Kas Umum (Tanpa Cabang)</SelectItem>
                {visible.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className={labelCls}>Keterangan</Label>
            <Input data-testid="receive-desc-input" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="Pelunasan / termin / DP" className={inputCls} />
          </div>
          <Button data-testid="receive-submit-button" type="submit" disabled={busy} className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-semibold">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan ke Buku Kas"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function MarkPaidDialog({ sale, onClose, onSaved }) {
  const [paidDate, setPaidDate] = useState(today());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (sale) setPaidDate(sale.paid_date || today());
  }, [sale]);

  if (!sale) return null;

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      await api.post(`/sales/${sale.id}/mark-paid`, { paid_date: paidDate });
      toast.success("Penjualan ditandai lunas — mobil Terjual");
      onClose();
      onSaved();
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!sale} onOpenChange={(value) => !value && onClose()}>
      <DialogContent className="bg-[#0A0A0C] border-[#1A1A22] text-zinc-100 max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold">Tandai Lunas</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-zinc-400 -mt-2">{sale.car_name}</p>
        <form onSubmit={submit} className="space-y-4" data-testid="mark-paid-form">
          <div className="space-y-2">
            <Label className={labelCls}>Tanggal Lunas</Label>
            <Input
              data-testid="mark-paid-date-input"
              type="date"
              required
              value={paidDate}
              onChange={(event) => setPaidDate(event.target.value)}
              className={inputCls}
            />
          </div>
          <Button
            data-testid="mark-paid-submit-button"
            type="submit"
            disabled={busy}
            className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-semibold"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan Tanggal Lunas"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PriceDialog({ sale, onClose, onSaved }) {
  const [price, setPrice] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (sale) setPrice("");
  }, [sale]);

  if (!sale) return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put(`/sales/${sale.id}`, { price: parseFloat(price) || 0 });
      toast.success("Harga jual final disimpan");
      onClose();
      onSaved();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!sale} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0A0A0C] border-[#1A1A22] text-zinc-100 max-w-sm">
        <DialogHeader><DialogTitle className="font-heading text-xl font-bold">Set Harga Jual Final</DialogTitle></DialogHeader>
        <p className="text-sm text-zinc-400 -mt-2">{sale.car_name}</p>
        <form onSubmit={submit} className="space-y-4" data-testid="price-form">
          <div className="space-y-2">
            <Label className={labelCls}>Harga Jual Final (Rp)</Label>
            <Input data-testid="price-final-input" type="number" min="1" required value={price} onChange={(e) => setPrice(e.target.value)} placeholder="185000000" className={inputCls} />
          </div>
          <Button data-testid="price-submit-button" type="submit" disabled={busy} className="w-full bg-blue-600 hover:bg-blue-500 text-white shadow-[0_0_12px_rgba(37,99,235,0.25)] font-semibold">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan Harga"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
